import { useLanguage } from '../../../features/language/useLanguage';
import type { Role } from '../../../services/memberService';

/** Title-cases a role name, e.g. "super admin" -> "Super Admin". */
export const formatRoleName = (name: string) =>
  name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Centralizes the member role/status label resolution shared by the filter bar,
 * invite form, member rows and the edit-role modal.
 *
 * @param roles full role list used to resolve a role's human `display_name`.
 */
export function useMemberLabels(roles: Role[] = []) {
  const { t } = useLanguage();

  // Fallback label when a role has no explicit display_name (translated + title-cased).
  const roleLabel = (role: string) => formatRoleName(t(`settingsMember.roles.${role}`, undefined, role));

  const statusLabel = (status: string) => t(`settingsMember.status.${status}`, undefined, status);

  // Prefer the backend-provided display_name, otherwise fall back to the translated label.
  const roleDisplay = (role: string) => roles.find((r) => r.name === role)?.display_name || roleLabel(role);

  return { roleLabel, statusLabel, roleDisplay };
}
